"use client";

import { isLivePreviewEvent, LivePreviewMessageEvent, mergeData } from "@payloadcms/live-preview";
import { useEffect, useRef, useState } from "react";

/**
 * Applies unsaved edits from the admin's live preview to a page's content docs. The admin sends its raw form state,
 * where relationships and uploads (e.g. images in rich text blocks) are only ids, so each message is sent back to
 * Payload to be populated before it replaces the doc.
 */
export function useLivePreviewContent<T extends { id: number }>(content: T[], collectionSlug: string, depth = 3): T[] {
    // Kept across new content: RefreshRouteOnSave refreshes the route on load and on save, and the form state stays
    // the newest version of the doc either way
    const [previewDocs, setPreviewDocs] = useState<Record<number, T>>({});
    // A message is sent on every keystroke; only the newest response may win. A ref, so it outlives the listener
    // being re-added when a refresh brings new content mid-request
    const latest = useRef(0);

    useEffect(() => {
        // The admin panel runs on the same origin as the page
        const serverURL = window.location.origin;

        const handleMessage = async (event: LivePreviewMessageEvent<T>) => {
            if (!isLivePreviewEvent(event, serverURL) || event.data.collectionSlug !== collectionSlug) return;
            const id = event.data.data?.id;
            if (!content.some((c) => c.id === id)) return;

            const request = ++latest.current;
            const doc = await mergeData<T>({
                collectionSlug,
                depth,
                incomingData: event.data.data,
                initialData: { id } as T,
                locale: event.data.locale,
                serverURL,
            }).catch(() => undefined);
            if (!doc || request !== latest.current) return;

            setPreviewDocs((prev) => ({ ...prev, [id]: doc }));
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [content, collectionSlug, depth]);

    return content.map((c) => previewDocs[c.id] ?? c);
}
